import { ChevronDown, Paperclip, Pin } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { downloadFile } from '@/api/files'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/format'
import { useMarkRead } from '../api'
import { audienceLabel, formatBytes, priorityMeta, relativeTime } from '../meta'
import type { FeedItem } from '../types'
import { AnnouncementBody } from './AnnouncementBody'
import { CategoryChip, CategoryTile } from './CategoryChip'

/**
 * One notice in a feed. Collapsed to a teaser; opening it (click / Enter) expands the full text
 * and records a read receipt.
 */
export function FeedCard({ item, defaultOpen = false }: { item: FeedItem; defaultOpen?: boolean }) {
  const { t } = useTranslation('announcements')
  const [open, setOpen] = useState(defaultOpen)
  const markRead = useMarkRead()
  const bodyId = useId()
  const priority = priorityMeta[item.priority]

  const toggle = () => {
    setOpen((o) => !o)
    if (!item.isRead && !markRead.isPending) markRead.mutate(item.id)
  }

  return (
    <article
      className={cn(
        'relative rounded-card border bg-surface shadow-card transition-shadow hover:shadow-raised',
        item.priority === 'urgent' ? 'border-danger-500/40' : 'border-line',
        !item.isRead && 'ring-1 ring-brand-200/70',
      )}
      aria-labelledby={`${bodyId}-title`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-start gap-3.5 rounded-card p-4 text-left sm:p-5"
      >
        <CategoryTile category={item.category} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <CategoryChip category={item.category} />
            {item.priority !== 'normal' ? (
              <Badge tone={priority.tone} size="sm">
                {t(priority.label)}
              </Badge>
            ) : null}
            {item.isPinned ? (
              <Badge tone="brand" size="sm">
                <Pin aria-hidden="true" /> {t('Pinned')}
              </Badge>
            ) : null}
          </div>
          <h3
            id={`${bodyId}-title`}
            className={cn('mt-1.5 text-[0.9375rem] text-ink', item.isRead ? 'font-medium' : 'font-semibold')}
          >
            {item.title}
          </h3>
          {!open ? <p className="mt-1 line-clamp-2 text-sm text-muted">{item.body}</p> : null}
          <p className="mt-2 text-xs text-subtle">
            <time dateTime={item.publishedAt} title={formatDateTime(item.publishedAt)}>
              {relativeTime(item.publishedAt)}
            </time>
            {' · '}
            {item.authorName}
            {' · '}
            {t(audienceLabel[item.audience])}
            {item.attachment ? (
              <>
                {' · '}
                <Paperclip className="inline size-3 align-[-2px]" aria-label={t('Has attachment')} />
              </>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {!item.isRead ? (
            <>
              <span className="size-2.5 rounded-full bg-brand-600" aria-hidden="true" />
              <span className="sr-only">{t('Unread')}</span>
            </>
          ) : null}
          <ChevronDown
            className={cn(
              'size-4 text-subtle transition-transform motion-reduce:transition-none',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </div>
      </button>
      {open ? (
        <div
          id={bodyId}
          className="animate-fade-in border-t border-line px-4 pt-4 pb-5 sm:px-5 sm:pl-[4.375rem]"
        >
          <AnnouncementBody text={item.body} />
          {item.attachment ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              leftIcon={<Paperclip />}
              onClick={() => {
                const file = item.attachment
                if (file)
                  downloadFile(file.downloadUrl, file.originalName).catch(() =>
                    toast.error(t('Download failed'), t('Please try again.')),
                  )
              }}
            >
              {item.attachment.originalName}{' '}
              <span className="text-muted">({formatBytes(item.attachment.size)})</span>
            </Button>
          ) : null}
          {item.expireAt ? (
            <p className="mt-4 text-xs text-subtle">
              {t('Visible until {{date}}', { date: formatDateTime(item.expireAt) })}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}
